import { useDialogs } from 'hooks/dialogs'
import { XSmallIcon } from '../icons/XSmall'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'

export function DialogCloseButton({
  onClick,
  className,
  iconClassName,
  buttonRef,
  onKeyDown,
  onKeyUp,
  closeOnMouseDown,
}: {
  className?: string
  iconClassName?: string
  buttonRef?: React.Ref<HTMLButtonElement>
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
} & (
  | {
      onClick?: (
        e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      ) => boolean
      /** use closeOnMouseDown ONLY if popDialog is idempotent, i.e. there is only one dialog! */
      closeOnMouseDown?: never
    }
  | {
      onClick?: never
      /** use closeOnMouseDown ONLY if popDialog is idempotent, i.e. there is only one dialog! */
      closeOnMouseDown?: boolean
    }
)) {
  const { popDialog } = useDialogs()
  const { t } = useTranslation()

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
      if (onClick) {
        const res = onClick(e)
        if (res) popDialog()
      } else {
        popDialog()
      }
    },
    [onClick, popDialog]
  )

  return (
    <div
      className={classnames(
        'absolute right-4 top-4 flex justify-end',
        className
      )}
      onClick={onClickHandler}
    >
      <button
        type="button"
        data-testid="dialog-close-button"
        className="focus-ring-double rounded-full p-1"
        ref={buttonRef}
        aria-label={t('dialogs.close_dialog')}
        onClick={onClickHandler}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onMouseDown={closeOnMouseDown ? popDialog : undefined}
      >
        <XSmallIcon aria-hidden size={22} className={iconClassName} />
      </button>
    </div>
  )
}
